import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Select,
  Button,
  Card,
  Space,
  Typography,
  Alert,
  Spin,
} from "antd";

import {
  DownloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";
import { useAcademicYear } from "../../../AcademicYearContext";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

// ============================================================
// COLORS
// ============================================================

const BLACK = "#111827";
const ORANGE = "#d35400";
const LIGHT_GRAY = "#f3f4f6";
const PAGE_GRAY = "#f5f6f8";
const GRAY = "#6b7280";
const BORDER_GRAY = "#e5e7eb";
const SOFT_ORANGE = "#fff7ed";
const ORANGE_BORDER = "#fed7aa";
const WHITE = "#ffffff";

// ============================================================
// PDF STYLES
// ============================================================

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 35,
    paddingHorizontal: 35,
    backgroundColor: WHITE,
    fontFamily: "Helvetica",
  },

  // ----------------------------------------------------------
  // HEADER
  // ----------------------------------------------------------

  header: {
    backgroundColor: LIGHT_GRAY,
    paddingVertical: 16,
    paddingHorizontal: 20,
    marginBottom: 18,
    borderBottomWidth: 4,
    borderBottomColor: ORANGE,
  },

  schoolName: {
    color: BLACK,
    fontSize: 19,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 5,
  },

  schoolSubtitle: {
    color: GRAY,
    fontSize: 9,
    textAlign: "center",
  },

  // ----------------------------------------------------------
  // TITLE
  // ----------------------------------------------------------

  titleContainer: {
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: ORANGE_BORDER,
  },

  title: {
    color: BLACK,
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 5,
  },

  className: {
    color: ORANGE,
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "center",
  },

  academicYear: {
    color: GRAY,
    fontSize: 9,
    textAlign: "center",
    marginTop: 4,
  },

  // ----------------------------------------------------------
  // TABLE
  // ----------------------------------------------------------

  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: BORDER_GRAY,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: ORANGE,
    borderBottomWidth: 1,
    borderBottomColor: ORANGE,
  },

  tableRow: {
    flexDirection: "row",
    minHeight: 30,
    borderBottomWidth: 1,
    borderBottomColor: BORDER_GRAY,
  },

  tableRowAlternate: {
    flexDirection: "row",
    minHeight: 30,
    backgroundColor: LIGHT_GRAY,
    borderBottomWidth: 1,
    borderBottomColor: BORDER_GRAY,
  },

  cell: {
    paddingVertical: 7,
    paddingHorizontal: 6,
    fontSize: 9,
    color: BLACK,
    justifyContent: "center",
  },

  headerCell: {
    paddingVertical: 8,
    paddingHorizontal: 6,
    fontSize: 9,
    fontWeight: "bold",
    color: WHITE,
    justifyContent: "center",
  },

  numberCell: {
    width: "8%",
    textAlign: "center",
  },

  nameCell: {
    width: "37%",
  },

  genderCell: {
    width: "15%",
    textAlign: "center",
  },

  classCell: {
    width: "20%",
    textAlign: "center",
  },

  statusCell: {
    width: "20%",
    textAlign: "center",
  },

  // ----------------------------------------------------------
  // SUMMARY
  // ----------------------------------------------------------

  summaryContainer: {
    marginTop: 15,
    padding: 12,
    backgroundColor: LIGHT_GRAY,
    borderLeftWidth: 4,
    borderLeftColor: ORANGE,
  },

  summaryText: {
    color: BLACK,
    fontSize: 10,
    fontWeight: "bold",
  },

  // ----------------------------------------------------------
  // FOOTER
  // ----------------------------------------------------------

  footer: {
    position: "absolute",
    bottom: 15,
    left: 35,
    right: 35,
    borderTopWidth: 1,
    borderTopColor: ORANGE_BORDER,
    paddingTop: 6,
  },

  footerText: {
    color: GRAY,
    fontSize: 8,
    textAlign: "center",
  },
});

// ============================================================
// PDF DOCUMENT
// ============================================================

const StudentGradeClassPDF = ({
  students,
  gradeClassName,
  academicYearName,
}) => {
  const safeStudents = Array.isArray(students)
    ? students
    : [];

  return (
    <Document>
      <Page
        size="A4"
        orientation="portrait"
        style={styles.page}
      >
        {/* ==================================================
            HEADER
        ================================================== */}

        <View style={styles.header}>
          <Text style={styles.schoolName}>
            Salvation In Christ School System
          </Text>

          <Text style={styles.schoolSubtitle}>
            Students Admission Record
          </Text>
        </View>

        {/* ==================================================
            TITLE
        ================================================== */}

        <View style={styles.titleContainer}>
          <Text style={styles.title}>
            STUDENTS IN GRADE CLASS
          </Text>

          <Text style={styles.className}>
            {gradeClassName || "N/A"}
          </Text>

          <Text style={styles.academicYear}>
            Academic Year:{" "}
            {academicYearName || "N/A"}
          </Text>
        </View>

        {/* ==================================================
            TABLE
        ================================================== */}

        <View style={styles.table}>

          {/* TABLE HEADER */}

          <View style={styles.tableHeader}>
            <Text
              style={[
                styles.headerCell,
                styles.numberCell,
              ]}
            >
              NO.
            </Text>

            <Text
              style={[
                styles.headerCell,
                styles.nameCell,
              ]}
            >
              FULL NAME
            </Text>

            <Text
              style={[
                styles.headerCell,
                styles.genderCell,
              ]}
            >
              GENDER
            </Text>

            <Text
              style={[
                styles.headerCell,
                styles.classCell,
              ]}
            >
              GRADE CLASS
            </Text>

            <Text
              style={[
                styles.headerCell,
                styles.statusCell,
              ]}
            >
              STATUS
            </Text>
          </View>

          {/* TABLE ROWS */}

          {safeStudents.map(
            (student, index) => (
              <View
                key={
                  student?.id ||
                  index
                }
                style={
                  index % 2 === 0
                    ? styles.tableRow
                    : styles.tableRowAlternate
                }
                wrap={false}
              >
                <Text
                  style={[
                    styles.cell,
                    styles.numberCell,
                  ]}
                >
                  {index + 1}
                </Text>

                <Text
                  style={[
                    styles.cell,
                    styles.nameCell,
                  ]}
                >
                  {student?.full_name ||
                    "N/A"}
                </Text>

                <Text
                  style={[
                    styles.cell,
                    styles.genderCell,
                  ]}
                >
                  {student?.gender ||
                    "N/A"}
                </Text>

                <Text
                  style={[
                    styles.cell,
                    styles.classCell,
                  ]}
                >
                  {student?.grade_class_name ||
                    gradeClassName ||
                    "N/A"}
                </Text>

                <Text
                  style={[
                    styles.cell,
                    styles.statusCell,
                  ]}
                >
                  {student?.status ||
                    "N/A"}
                </Text>
              </View>
            )
          )}
        </View>

        {/* ==================================================
            SUMMARY
        ================================================== */}

        <View style={styles.summaryContainer}>
          <Text style={styles.summaryText}>
            Total Students:{" "}
            {safeStudents.length}
          </Text>
        </View>

        {/* ==================================================
            FOOTER
        ================================================== */}

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Polu Integrated School Management System
          </Text>
        </View>
      </Page>
    </Document>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const AdmissionStuListByClass = () => {
  const { selectedYear } =
    useAcademicYear();

  // ==========================================================
  // STATE
  // ==========================================================

  const [students, setStudents] =
    useState([]);

  const [
    selectedGradeClassId,
    setSelectedGradeClassId,
  ] = useState(null);

  const [loading, setLoading] =
    useState(false);

  const [downloading, setDownloading] =
    useState(false);

  const [error, setError] =
    useState("");

  // ==========================================================
  // TOKEN
  // ==========================================================

  const getToken = () => {
    return localStorage.getItem(
      "access"
    );
  };

  // ==========================================================
  // NORMALIZE API RESPONSE
  // ==========================================================

  const normalizeStudentsResponse = (
    data
  ) => {
    if (Array.isArray(data)) {
      return data;
    }

    if (
      Array.isArray(
        data?.results
      )
    ) {
      return data.results;
    }

    if (
      Array.isArray(
        data?.students
      )
    ) {
      return data.students;
    }

    if (
      Array.isArray(
        data?.data
      )
    ) {
      return data.data;
    }

    return [];
  };

  // ==========================================================
  // FETCH STUDENTS
  // ==========================================================

  const fetchStudents = async () => {
    const token = getToken();

    if (!token) {
      window.location.href =
        "/login";

      return;
    }

    if (!selectedYear?.id) {
      setStudents([]);

      return;
    }

    try {
      setLoading(true);
      setError("");

      const config = {
        headers: {
          Authorization:
            `Bearer ${token}`,
        },
      };

      const response =
        await api.get(
          `/api/users/students/?academic_year=${selectedYear.id}`,
          config
        );

      const normalizedStudents =
        normalizeStudentsResponse(
          response.data
        );

      setStudents(
        normalizedStudents
      );

      setSelectedGradeClassId(
        null
      );

    } catch (err) {
      console.error(
        "Error loading students:",
        err
      );

      if (
        err.response?.status ===
        401
      ) {
        localStorage.removeItem(
          "access"
        );

        window.location.href =
          "/login";

        return;
      }

      setStudents([]);

      setError(
        err.response?.data
          ?.detail ||
          "Unable to load students."
      );

    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // LOAD WHEN ACADEMIC YEAR CHANGES
  // ==========================================================

  useEffect(() => {
    setSelectedGradeClassId(
      null
    );

    setStudents([]);

    if (selectedYear?.id) {
      fetchStudents();
    }
  }, [selectedYear]);

  // ==========================================================
  // GET UNIQUE GRADE CLASSES
  // ==========================================================

  const gradeClassOptions =
    useMemo(() => {
      const safeStudents =
        Array.isArray(
          students
        )
          ? students
          : [];

      const classesMap =
        new Map();

      safeStudents.forEach(
        (student) => {
          if (
            student?.grade_class ===
              null ||
            student?.grade_class ===
              undefined
          ) {
            return;
          }

          const classId =
            student.grade_class;

          const className =
            student.grade_class_name ||
            `Grade Class ${classId}`;

          if (
            !classesMap.has(
              String(classId)
            )
          ) {
            classesMap.set(
              String(classId),
              {
                value: classId,
                label: className,
                searchText:
                  String(
                    className
                  ).toLowerCase(),
              }
            );
          }
        }
      );

      return Array.from(
        classesMap.values()
      ).sort(
        (a, b) =>
          a.label.localeCompare(
            b.label,
            undefined,
            {
              numeric: true,
              sensitivity:
                "base",
            }
          )
      );
    }, [students]);

  // ==========================================================
  // SELECTED GRADE CLASS
  // ==========================================================

  const selectedGradeClass =
    useMemo(() => {
      if (
        selectedGradeClassId ===
          null ||
        selectedGradeClassId ===
          undefined
      ) {
        return null;
      }

      const safeStudents =
        Array.isArray(
          students
        )
          ? students
          : [];

      const matchingStudent =
        safeStudents.find(
          (student) =>
            String(
              student.grade_class
            ) ===
            String(
              selectedGradeClassId
            )
        );

      if (!matchingStudent) {
        return null;
      }

      return {
        id:
          matchingStudent.grade_class,

        name:
          matchingStudent.grade_class_name ||
          `Grade Class ${matchingStudent.grade_class}`,
      };
    }, [
      students,
      selectedGradeClassId,
    ]);

  // ==========================================================
  // STUDENTS IN SELECTED GRADE CLASS
  // ==========================================================

  const studentsInSelectedGrade =
    useMemo(() => {
      if (
        selectedGradeClassId ===
          null ||
        selectedGradeClassId ===
          undefined
      ) {
        return [];
      }

      const safeStudents =
        Array.isArray(
          students
        )
          ? students
          : [];

      return safeStudents.filter(
        (student) =>
          String(
            student?.grade_class
          ) ===
          String(
            selectedGradeClassId
          )
      );
    }, [
      students,
      selectedGradeClassId,
    ]);

  // ==========================================================
  // DOWNLOAD PDF
  // ==========================================================

  const handleDownloadPDF =
    async () => {
      if (
        selectedGradeClassId ===
          null ||
        selectedGradeClassId ===
          undefined
      ) {
        setError(
          "Please select a Grade Class first."
        );

        return;
      }

      const safeStudents =
        Array.isArray(
          studentsInSelectedGrade
        )
          ? studentsInSelectedGrade
          : [];

      if (
        safeStudents.length ===
        0
      ) {
        setError(
          "No students were found in the selected Grade Class."
        );

        return;
      }

      try {
        setDownloading(true);
        setError("");

        const gradeClassName =
          selectedGradeClass?.name ||
          "Grade Class";

        // ----------------------------------------------------
        // CREATE PDF
        // ----------------------------------------------------

        const blob =
          await pdf(
            <StudentGradeClassPDF
              students={
                safeStudents
              }
              gradeClassName={
                gradeClassName
              }
              academicYearName={
                selectedYear?.name
              }
            />
          ).toBlob();

        // ----------------------------------------------------
        // DOWNLOAD PDF
        // ----------------------------------------------------

        const url =
          URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href = url;

        link.download =
          `${gradeClassName
            .replace(
              /[^a-z0-9]/gi,
              "_"
            )
            .replace(
              /_+/g,
              "_"
            )}_Students.pdf`;

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        URL.revokeObjectURL(
          url
        );

      } catch (err) {
        console.error(
          "PDF generation error:",
          err
        );

        setError(
          "Unable to generate the PDF. Please try again."
        );

      } finally {
        setDownloading(false);
      }
    };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={{
        width: "100%",
        backgroundColor:
          PAGE_GRAY,
        padding:
          "8px 12px",
        boxSizing:
          "border-box",
      }}
    >
      <div
        style={{
          maxWidth: 720,
          margin: "0 auto",
        }}
      >
        <Card
          bordered={false}
          style={{
            borderRadius: 14,
            backgroundColor:
              WHITE,
            border:
              `1px solid ${BORDER_GRAY}`,
            boxShadow:
              "0 3px 14px rgba(17, 24, 39, 0.07)",
            overflow:
              "hidden",
          }}
          styles={{
            body: {
              padding: 0,
            },
          }}
        >
          {/* ==================================================
              HEADER
          ================================================== */}

          <div
            style={{
              backgroundColor:
                LIGHT_GRAY,
              padding:
                "15px 20px 13px",
              borderBottom:
                `1px solid ${BORDER_GRAY}`,
            }}
          >
            <div
              style={{
                display:
                  "flex",
                alignItems:
                  "center",
                gap: 12,
              }}
            >
              {/* ICON */}

              <div
                style={{
                  width: 42,
                  height: 42,
                  minWidth: 42,
                  borderRadius: 10,
                  backgroundColor:
                    "#fff1e8",
                  border:
                    `1px solid ${ORANGE_BORDER}`,
                  display:
                    "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                }}
              >
                <TeamOutlined
                  style={{
                    fontSize: 21,
                    color: ORANGE,
                  }}
                />
              </div>

              {/* TITLE */}

              <div
                style={{
                  flex: 1,
                }}
              >
                <Typography.Title
                  level={4}
                  style={{
                    margin: 0,
                    marginBottom: 3,
                    color: BLACK,
                    fontSize: 20,
                    fontWeight: 700,
                    lineHeight:
                      1.25,
                  }}
                >
                  Download Grade Class
                  Students
                </Typography.Title>

                <Typography.Text
                  style={{
                    color: GRAY,
                    fontSize: 12.5,
                  }}
                >
                  Select a grade class
                  to generate the
                  complete student
                  list.
                </Typography.Text>
              </div>
            </div>
          </div>

          {/* ==================================================
              CONTENT
          ================================================== */}

          <div
            style={{
              padding:
                "15px 20px 17px",
            }}
          >
            {/* ==================================================
                ERROR
            ================================================== */}

            {error && (
              <Alert
                type="error"
                showIcon
                closable
                message={error}
                onClose={() =>
                  setError("")
                }
                style={{
                  marginBottom: 12,
                  borderRadius: 8,
                }}
              />
            )}

            {/* ==================================================
                LOADING
            ================================================== */}

            {loading ? (
              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "center",
                  alignItems:
                    "center",
                  padding:
                    "22px 0",
                }}
              >
                <Space
                  direction="vertical"
                  align="center"
                  size={5}
                >
                  <Spin
                    size="default"
                  />

                  <Typography.Text
                    style={{
                      color:
                        GRAY,
                      fontSize: 13,
                    }}
                  >
                    Loading
                    students...
                  </Typography.Text>
                </Space>
              </div>
            ) : (
              <>
                {/* ==================================================
                    GRADE CLASS SELECTOR
                ================================================== */}

                <div
                  style={{
                    marginBottom:
                      12,
                  }}
                >
                  <Typography.Text
                    strong
                    style={{
                      display:
                        "block",
                      marginBottom:
                        6,
                      color:
                        BLACK,
                      fontSize:
                        13,
                    }}
                  >
                    Select Grade Class
                  </Typography.Text>

                  <Select
                    showSearch
                    allowClear
                    size="large"
                    placeholder="Search and select a Grade Class"
                    prefix={
                      <TeamOutlined
                        style={{
                          color:
                            ORANGE,
                          fontSize:
                            16,
                        }}
                      />
                    }
                    value={
                      selectedGradeClassId
                    }
                    options={
                      gradeClassOptions
                    }
                    style={{
                      width:
                        "100%",
                    }}
                    filterOption={(
                      input,
                      option
                    ) =>
                      option?.searchText?.includes(
                        input.toLowerCase()
                      )
                    }
                    onChange={(
                      value
                    ) => {
                      setSelectedGradeClassId(
                        value ??
                          null
                      );

                      setError("");
                    }}
                    notFoundContent={
                      <Typography.Text
                        style={{
                          color:
                            GRAY,
                          fontSize:
                            13,
                        }}
                      >
                        No Grade
                        Classes
                        found
                      </Typography.Text>
                    }
                  />
                </div>

                {/* ==================================================
                    NO STUDENTS
                ================================================== */}

                {!loading &&
                  students.length ===
                    0 &&
                  !error && (
                    <Alert
                      type="info"
                      showIcon
                      message="No students found"
                      description={
                        selectedYear?.id
                          ? "There are no students available for the selected academic year."
                          : "Please select an academic year."
                      }
                      style={{
                        marginBottom:
                          12,
                        borderRadius:
                          8,
                      }}
                    />
                  )}

                {/* ==================================================
                    SELECTED GRADE CLASS
                ================================================== */}

                {selectedGradeClass && (
                  <div
                    style={{
                      marginBottom:
                        12,
                      padding:
                        "11px 13px",
                      borderRadius:
                        9,
                      backgroundColor:
                        SOFT_ORANGE,
                      border:
                        `1px solid ${ORANGE_BORDER}`,
                      borderLeft:
                        `4px solid ${ORANGE}`,
                    }}
                  >
                    <div
                      style={{
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "space-between",
                        gap: 10,
                        flexWrap:
                          "wrap",
                      }}
                    >
                      {/* CLASS */}

                      <div
                        style={{
                          display:
                            "flex",
                          alignItems:
                            "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius:
                              7,
                            backgroundColor:
                              WHITE,
                            display:
                              "flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "center",
                            border:
                              `1px solid ${ORANGE_BORDER}`,
                          }}
                        >
                          <TeamOutlined
                            style={{
                              color:
                                ORANGE,
                              fontSize:
                                15,
                            }}
                          />
                        </div>

                        <div>
                          <Typography.Text
                            style={{
                              display:
                                "block",
                              color:
                                GRAY,
                              fontSize:
                                10,
                              lineHeight:
                                1.2,
                              textTransform:
                                "uppercase",
                              letterSpacing:
                                "0.5px",
                            }}
                          >
                            Grade Class
                          </Typography.Text>

                          <Typography.Text
                            strong
                            style={{
                              display:
                                "block",
                              color:
                                BLACK,
                              fontSize:
                                14,
                            }}
                          >
                            {
                              selectedGradeClass.name
                            }
                          </Typography.Text>
                        </div>
                      </div>

                      {/* ACADEMIC YEAR */}

                      <div
                        style={{
                          minWidth:
                            115,
                        }}
                      >
                        <Typography.Text
                          style={{
                            display:
                              "block",
                            color:
                              GRAY,
                            fontSize:
                              10,
                            lineHeight:
                              1.2,
                            textTransform:
                              "uppercase",
                            letterSpacing:
                              "0.5px",
                          }}
                        >
                          Academic Year
                        </Typography.Text>

                        <Typography.Text
                          strong
                          style={{
                            color:
                              BLACK,
                            fontSize:
                              13,
                          }}
                        >
                          {selectedYear?.name ||
                            "N/A"}
                        </Typography.Text>
                      </div>

                      {/* STUDENT COUNT */}

                      <div
                        style={{
                          display:
                            "flex",
                          alignItems:
                            "center",
                          gap: 7,
                          padding:
                            "5px 9px",
                          borderRadius:
                            7,
                          backgroundColor:
                            WHITE,
                          border:
                            `1px solid ${ORANGE_BORDER}`,
                        }}
                      >
                        <Typography.Text
                          style={{
                            color:
                              GRAY,
                            fontSize:
                              11,
                          }}
                        >
                          Students
                        </Typography.Text>

                        <Typography.Text
                          strong
                          style={{
                            color:
                              ORANGE,
                            fontSize:
                              16,
                          }}
                        >
                          {
                            studentsInSelectedGrade.length
                          }
                        </Typography.Text>
                      </div>
                    </div>
                  </div>
                )}

                {/* ==================================================
                    DOWNLOAD BUTTON
                ================================================== */}

                <Button
                  type="primary"
                  size="large"
                  block
                  icon={
                    <DownloadOutlined />
                  }
                  loading={
                    downloading
                  }
                  disabled={
                    !selectedGradeClassId ||
                    studentsInSelectedGrade.length ===
                      0
                  }
                  onClick={
                    handleDownloadPDF
                  }
                  style={{
                    height: 44,
                    borderRadius: 8,
                    backgroundColor:
                      ORANGE,
                    borderColor:
                      ORANGE,
                    fontWeight:
                      600,
                    fontSize: 14,
                    boxShadow:
                      "0 3px 8px rgba(211, 84, 0, 0.20)",
                  }}
                >
                  {downloading
                    ? "Generating PDF..."
                    : "Download All Students in This Grade Class"}
                </Button>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdmissionStuListByClass;